/*!
 * Copyright © 2005-2023 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { APP_INITIALIZER, isDevMode, Provider, Type } from '@angular/core';
import { ExtensionService, RuleEvaluator } from '@alfresco/adf-extensions';
import { AlgCommentsTabComponent } from './components/comments-tab/alg-comments-tab.component';

/**
 * Surcharges des composants et des regles d'ACA, par identifiant.
 *
 * `setComponents()` et `setEvaluators()` font tous deux un `Object.assign` : la derniere
 * declaration gagne. C'est ce qui nous permet de remplacer un element d'ACA SANS modifier
 * `projects/aca-content` ni `projects/aca-shared` — voir docs/fork/README.md §8.
 *
 * Pourquoi un `APP_INITIALIZER` et pas le constructeur du module : `ContentServiceExtensionModule`
 * (ACA) est enregistre APRES nos extensions par le bootstrap standalone
 * (`app/src/app/app.config.ts`). Enregistrer depuis notre constructeur de module se ferait donc
 * ecraser par ACA. Un `APP_INITIALIZER` s'execute apres
 * tous les constructeurs de modules : l'override est garanti sans dependre de l'ordre des imports.
 *
 * ATTENTION : si un identifiant disparait d'ACA lors d'une montee de version, la surcharge devient
 * silencieusement inoperante. `scripts/check-overrides.js` est la pour detecter exactement ca —
 * tenir ses tables `EXPECTED_*` a jour.
 */

/** Ids disponibles : `extensions.setComponents({...})` dans `aca-content.module.ts`. */
export const ALG_COMPONENT_OVERRIDES: { [key: string]: Type<any> } = {
  // Onglet "Commentaires" : rc-comments (edition, suppression, compteur synchronise)
  // au lieu de adf-node-comments.
  'app.components.tabs.comments': AlgCommentsTabComponent
};

/** Ids disponibles : `extensions.setEvaluators({...})` dans `aca-content.module.ts`. */
export const ALG_EVALUATOR_OVERRIDES: { [key: string]: RuleEvaluator } = {
  // Vide depuis le palier ACA 8.0.0.
  //
  // Nous surchargions 'app.navigation.folder.canUpload' pour elargir le droit d'upload a tout
  // dossier ou l'utilisateur peut creer. Upstream a CONVERGE sur ce comportement : il a introduit
  // 'app.navigation.folder.canCreate' -> canCreateFolder des 4.4.1, puis supprime canUpload en
  // 7.5.0. En 8.0.0 l'identifiant n'existe plus nulle part et app.extensions.json utilise
  // canCreate partout. La surcharge etait donc devenue sans effet : elle est retiree.
};

export function provideAlgAcaOverrides(): Provider {
  return {
    provide: APP_INITIALIZER,
    multi: true,
    deps: [ExtensionService],
    useFactory: (extensions: ExtensionService) => () => {
      extensions.setComponents(ALG_COMPONENT_OVERRIDES);
      if (Object.keys(ALG_EVALUATOR_OVERRIDES).length > 0) {
        extensions.setEvaluators(ALG_EVALUATOR_OVERRIDES);
      }

      // En mode dev uniquement : expose le registre pour que scripts/check-overrides.js
      // puisse verifier, sans authentification, quel composant / quelle regle gagne.
      if (isDevMode()) {
        (window as any).__algExtensions = extensions;
      }
    }
  };
}
